// TradeHistory/PnLChart.jsx
import React, { useMemo } from 'react';
import { Box, Stack, Typography, alpha } from '@mui/material';
import { ShowChart as ChartIcon } from '@mui/icons-material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { COLORS, formatPnL, formatShortDate } from './constants';

// Custom Tooltip
const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const data = payload[0].payload;

  return (
    <Box
      sx={{
        bgcolor: 'rgba(0, 0, 0, 0.9)',
        border: `1px solid ${alpha(COLORS.primary, 0.3)}`,
        borderRadius: 1.5,
        p: 1.5,
        backdropFilter: 'blur(10px)',
      }}
    >
      <Typography
        sx={{
          color: COLORS.text.muted,
          fontSize: '0.7rem',
          mb: 0.5,
        }}
      >
        Trade #{data.tradeNumber} • {data.date}
      </Typography>
      <Typography
        sx={{
          color: data.cumulative >= 0 ? COLORS.success : COLORS.danger,
          fontSize: '1rem',
          fontWeight: 700,
          fontFamily: 'monospace',
        }}
      >
        {formatPnL(data.cumulative)}
      </Typography>
      <Typography
        sx={{
          color: data.pnl >= 0 ? COLORS.success : COLORS.danger,
          fontSize: '0.75rem',
          fontFamily: 'monospace',
          opacity: 0.8,
        }}
      >
        Trade: {formatPnL(data.pnl)}
      </Typography>
    </Box>
  );
};

const PnLChart = ({ trades, height = 350 }) => {
  const chartData = useMemo(() => {
    if (!trades?.length) return [];

    let cumulative = 0;
    return trades
      .filter((t) => t.pnl !== null && t.pnl !== undefined)
      .map((trade, index) => {
        const pnl = parseFloat(trade.pnl) || 0;
        cumulative += pnl;
        return {
          tradeNumber: trade.trade_number || index + 1,
          date: formatShortDate(trade.entry_time),
          pnl,
          cumulative,
        };
      });
  }, [trades]);

  // Empty state
  if (!chartData.length) {
    return (
      <Box
        sx={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: alpha(COLORS.bg.tertiary, 0.3),
          borderRadius: 2,
          border: `1px dashed ${alpha(COLORS.border, 0.5)}`,
        }}
      >
        <Stack alignItems="center" spacing={1.5}>
          <ChartIcon sx={{ fontSize: 48, color: alpha(COLORS.text.muted, 0.3) }} />
          <Typography sx={{ color: COLORS.text.muted, fontSize: '0.9rem' }}>
            No P&L data available
          </Typography>
        </Stack>
      </Box>
    );
  }

  const maxVal = Math.max(...chartData.map((d) => d.cumulative));
  const minVal = Math.min(...chartData.map((d) => d.cumulative));
  const padding = Math.max(Math.abs(maxVal), Math.abs(minVal)) * 0.2 || 50;

  const finalCumulative = chartData[chartData.length - 1]?.cumulative || 0;
  const isPositive = finalCumulative >= 0;
  const lineColor = isPositive ? COLORS.success : COLORS.danger;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart
        data={chartData}
        margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
      >
        <defs>
          <linearGradient id="gradientGreen" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={COLORS.success} stopOpacity={0.25} />
            <stop offset="100%" stopColor={COLORS.success} stopOpacity={0} />
          </linearGradient>
          <linearGradient id="gradientRed" x1="0" y1="1" x2="0" y2="0">
            <stop offset="0%" stopColor={COLORS.danger} stopOpacity={0.25} />
            <stop offset="100%" stopColor={COLORS.danger} stopOpacity={0} />
          </linearGradient>
        </defs>

        <CartesianGrid
          strokeDasharray="3 3"
          stroke={alpha(COLORS.text.muted, 0.08)}
          vertical={false}
        />

        <XAxis
          dataKey="tradeNumber"
          tick={{ fill: COLORS.text.muted, fontSize: 10 }}
          axisLine={{ stroke: alpha(COLORS.text.muted, 0.1) }}
          tickLine={false}
        />

        <YAxis
          tick={{ fill: COLORS.text.muted, fontSize: 10 }}
          axisLine={false}
          tickLine={false}
          tickFormatter={(v) => `$${v.toLocaleString()}`}
          domain={[Math.floor(minVal - padding), Math.ceil(maxVal + padding)]}
          width={60}
        />

        <RechartsTooltip content={<ChartTooltip />} />

        <ReferenceLine
          y={0}
          stroke={alpha(COLORS.text.muted, 0.2)}
          strokeDasharray="4 4"
        />

        <Area
          type="monotone"
          dataKey="cumulative"
          stroke={lineColor}
          strokeWidth={2}
          fill={isPositive ? 'url(#gradientGreen)' : 'url(#gradientRed)'}
          dot={false}
          activeDot={{
            r: 5,
            strokeWidth: 2,
            stroke: '#fff',
            fill: lineColor,
          }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default PnLChart;
